import * as SQLite from 'expo-sqlite'
import { DATABASE_NAME } from './DatabaseConstants'
import { TABLE_NAME, TABLES_SCHEMAS } from './schemas'

class DatabaseSingleton {
    private static instance: SQLite.SQLiteDatabase | null = null
    private static tablesInitialized: boolean = false
    private static initializationPromise: Promise<void> | null = null

    private constructor() {}

    // llama a la base de datos (síncrono porque openDatabaseSync es síncrono)
    public static getInstance(): SQLite.SQLiteDatabase {
        if (!DatabaseSingleton.instance) {
            DatabaseSingleton.instance = SQLite.openDatabaseSync(DATABASE_NAME)

            // ✅ Habilitar WAL mode para mejor concurrencia
            // WAL permite lecturas y escrituras simultáneas sin bloqueos
            try {
                DatabaseSingleton.instance.execSync('PRAGMA journal_mode = WAL;')
                DatabaseSingleton.instance.execSync('PRAGMA synchronous = NORMAL;')
                // Aumentar timeout para escrituras concurrentes
                DatabaseSingleton.instance.execSync('PRAGMA busy_timeout = 5000;')
                console.log(
                    '✅ [DatabaseSingleton] WAL mode enabled for better concurrency',
                )
            } catch (error) {
                console.warn(
                    '⚠️ [DatabaseSingleton] Could not enable WAL mode:',
                    error,
                )
            }
        }

        return DatabaseSingleton.instance
    }

    // inicializa las tablas (solo una vez)
    public static async initializeTables(): Promise<void> {
        // Si ya hay una inicialización en progreso, esperar a que termine
        if (DatabaseSingleton.initializationPromise) {
            return DatabaseSingleton.initializationPromise
        }

        if (DatabaseSingleton.tablesInitialized) {
            return // Ya se inicializaron
        }

        // Crear la promesa de inicialización
        DatabaseSingleton.initializationPromise = (async () => {
            const db = DatabaseSingleton.getInstance()
            console.log(
                `🔧 [DatabaseSingleton] Iniciando creación de ${TABLES_SCHEMAS.length} tablas...`,
            )

            try {
                // Ejecutar cada CREATE TABLE por separado para mejor debugging
                for (let i = 0; i < TABLES_SCHEMAS.length; i++) {
                    const table = TABLES_SCHEMAS[i]
                    try {
                        //Valido si la tabla ya existia antes de la creación
                        const result = await db.getFirstAsync(
                            "SELECT name FROM sqlite_master WHERE type='table' AND name=?",
                            [table.name],
                        )
                        const alreadyExists = !!result

                        await db.execAsync(table.schema)
                        if (!alreadyExists) {
                            console.log(
                                `✅ [${i + 1}/${TABLES_SCHEMAS.length}] Tabla creada: ${table.name}`,
                            )
                        }
                    } catch (tableError) {
                        console.error(
                            `❌ [${i + 1}/${TABLES_SCHEMAS.length}] Error creando tabla ${table.name}:`,
                            tableError,
                        )
                        console.error(
                            `Schema que falló:`,
                            table.schema.substring(0, 200),
                        )
                        // No relanzamos: si una tabla falla, seguimos con el resto
                        // para no dejar sin migraciones a las tablas que sí existen
                        // (ver runMigrations más abajo).
                    }
                }

                console.log(
                    '✅ [DatabaseSingleton] Todas las tablas creadas exitosamente',
                )

                // 🔄 Ejecutar migraciones para usuarios existentes
                await DatabaseSingleton.runMigrations(db)

                DatabaseSingleton.tablesInitialized = true
            } catch (error) {
                console.error('❌ [DatabaseSingleton] Error creando tablas:', error)
                DatabaseSingleton.initializationPromise = null // Reset para poder reintentar
                throw error
            }
        })()

        return DatabaseSingleton.initializationPromise
    }

    /**
     * 🔄 Ejecuta migraciones necesarias para usuarios existentes
     */
    private static async runMigrations(db: SQLite.SQLiteDatabase): Promise<void> {
        try {
            // Migración: Agregar columnas faltantes a user_profiles
            const userProfileMigrations = [
                'ALTER TABLE user_profiles ADD COLUMN last_update_coords TEXT;',
                'ALTER TABLE user_profiles ADD COLUMN area TEXT;',
                'ALTER TABLE user_profiles ADD COLUMN nickname TEXT;',
                'ALTER TABLE user_profiles ADD COLUMN dni TEXT;',
            ]

            for (const migration of userProfileMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            `✅ [Migration] Column added to user_profiles: ${migration.split('ADD COLUMN ')[1].split(';')[0]}`,
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message.includes('duplicate column name')
                        ) {
                            console.log(
                                `⚠️ [Migration] user_profiles migration skipped: ${e.message}`,
                            )
                        }
                    })
            }
            // Migración: Agregar columnas faltantes a cursos
            const learningMigrations = [
                'ALTER TABLE learning_courses ADD COLUMN download_zip TEXT',
                'ALTER TABLE learning_categories ADD COLUMN category_index INTEGER',
                'ALTER TABLE learning_evaluations ADD COLUMN approval_rate TEXT',
                'ALTER TABLE answers_learning_evaluations ADD COLUMN evaluation_id TEXT',
                'ALTER TABLE learning_evaluations ADD COLUMN attempt_expiry_days INTEGER',
                'ALTER TABLE learning_evaluations ADD COLUMN attempt_expiry_minutes INTEGER',
                'ALTER TABLE learning_evaluations ADD COLUMN attempt_reset_days INTEGER',
                'ALTER TABLE learning_evaluations ADD COLUMN attempt_reset_minutes INTEGER',
                'ALTER TABLE learning_evaluations ADD COLUMN attempt_date_created TEXT',
            ]

            for (const migration of learningMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            `✅ [Migration] Column added to learningMigrations: ${migration.split('ADD COLUMN ')[1].split(';')[0]}`,
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message.includes('duplicate column name')
                        ) {
                            console.log(
                                `⚠️ [Migration] learningMigrations migration skipped: ${e.message}`,
                            )
                        }
                    })
            }
            // Migración: Agregar columnas link, objective, weigh a stores_forms
            const formsMigrations = [
                'ALTER TABLE stores_forms ADD COLUMN link TEXT;',
                'ALTER TABLE stores_forms ADD COLUMN objective INTEGER;',
                'ALTER TABLE stores_forms ADD COLUMN weigh INTEGER;',
                'ALTER TABLE stores_forms ADD COLUMN scores TEXT;',
                'ALTER TABLE stores_forms ADD COLUMN weights TEXT;',
                'ALTER TABLE stores_forms ADD COLUMN sub_title TEXT;',
                'ALTER TABLE stores_forms ADD COLUMN description TEXT;',
            ]

            for (const migration of formsMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            `✅ [Migration] Column added to stores_forms: ${migration.split('ADD COLUMN ')[1].split(';')[0]}`,
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message.includes('duplicate column name')
                        ) {
                            console.log(
                                `⚠️ [Migration] stores_forms migration skipped: ${e.message}`,
                            )
                        }
                    })
            }

            // Migración: Agregar columnas a stores
            const storesMigrations = [
                'ALTER TABLE stores ADD COLUMN existsInDB BOOLEAN;',
                'ALTER TABLE stores ADD COLUMN organization_id TEXT;',
                'ALTER TABLE stores ADD COLUMN status_send INTEGER;',
                'ALTER TABLE stores ADD COLUMN _id_mongoDB TEXT;',
                'ALTER TABLE stores ADD COLUMN frequency TEXT;',
                'ALTER TABLE stores ADD COLUMN topstore TEXT;',
                'ALTER TABLE stores ADD COLUMN potencial TEXT;',
            ]

            for (const migration of storesMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            `✅ [Migration] Column added to stores: ${migration.split('ADD COLUMN ')[1].split(';')[0]}`,
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message.includes('duplicate column name')
                        ) {
                            console.log(
                                `⚠️ [Migration] stores migration skipped: ${e.message}`,
                            )
                        }
                    })
            }

            // Migración: Agregar columnas a schedules_pos
            const schedulesPosMigrations = [
                'ALTER TABLE schedules_pos ADD COLUMN existsInDB BOOLEAN;',
                'ALTER TABLE schedules_pos ADD COLUMN status_send INTEGER;',
            ]

            for (const migration of schedulesPosMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            `✅ [Migration] Column added to schedules_pos: ${migration.split('ADD COLUMN ')[1].split(';')[0]}`,
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message.includes('duplicate column name')
                        ) {
                            console.log(
                                `⚠️ [Migration] schedules_pos migration skipped: ${e.message}`,
                            )
                        }
                    })
            }

            // Migración: Agregar comment_count a posts
            await db
                .execAsync('ALTER TABLE posts ADD COLUMN comment_count INTEGER;')
                .then(() => {
                    console.log('✅ [Migration] comment_count column added to posts')
                })
                .catch((e) => {
                    if (e.message && !e.message.includes('duplicate column name')) {
                        console.log(
                            '⚠️ [Migration] posts comment_count migration skipped:',
                            e.message,
                        )
                    }
                })

            // Migración: Agregar isPinned a posts
            await db
                .execAsync(
                    'ALTER TABLE posts ADD COLUMN isPinned BOOLEAN DEFAULT 0;',
                )
                .then(() => {
                    console.log('✅ [Migration] isPinned column added to posts')
                })
                .catch((e) => {
                    if (e.message && !e.message.includes('duplicate column name')) {
                        console.log(
                            '⚠️ [Migration] posts isPinned migration skipped:',
                            e.message,
                        )
                    }
                })

            // Migración: Agregar post_height a posts
            await db
                .execAsync('ALTER TABLE posts ADD COLUMN post_height INTEGER;')
                .then(() => {
                    console.log('✅ [Migration] post_height column added to posts')
                })
                .catch((e) => {
                    if (e.message && !e.message.includes('duplicate column name')) {
                        console.log(
                            '⚠️ [Migration] posts post_height migration skipped:',
                            e.message,
                        )
                    }
                })

            // Migración: Agregar a schedules_advances
            const schedulesAdvancedMigrations = [
                'ALTER TABLE schedules_advances ADD COLUMN incidence_answer TEXT;',
                'ALTER TABLE schedules_advances ADD COLUMN last_checkout TEXT;',
                'ALTER TABLE schedules_advances ADD COLUMN score_global REAL;',
            ]

            for (const migration of schedulesAdvancedMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            '✅ [Migration]  column added to schedules_advances',
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message.includes('duplicate column name')
                        ) {
                            console.log(
                                '⚠️ [Migration] schedules_advances migration skipped:',
                                e.message,
                            )
                        }
                    })
            }

            // Migración: Agregar objectives, steps_distributor, formsIdsFqa a transaction_checkins
            await db
                .execAsync(
                    'ALTER TABLE transaction_checkins ADD COLUMN objectives TEXT;',
                )
                .then(() =>
                    console.log(
                        '✅ [Migration] objectives column added to transaction_checkins',
                    ),
                )
                .catch((e) => {
                    if (e.message && !e.message.includes('duplicate column name')) {
                        console.log(
                            '⚠️ [Migration] transaction_checkins objectives migration skipped:',
                            e.message,
                        )
                    }
                })

            await db
                .execAsync(
                    'ALTER TABLE transaction_checkins ADD COLUMN steps_distributor TEXT;',
                )
                .then(() =>
                    console.log(
                        '✅ [Migration] steps_distributor column added to transaction_checkins',
                    ),
                )
                .catch((e) => {
                    if (e.message && !e.message.includes('duplicate column name')) {
                        console.log(
                            '⚠️ [Migration] transaction_checkins steps_distributor migration skipped:',
                            e.message,
                        )
                    }
                })

            await db
                .execAsync(
                    'ALTER TABLE transaction_checkins ADD COLUMN formsIdsFqa TEXT;',
                )
                .then(() =>
                    console.log(
                        '✅ [Migration] formsIdsFqa column added to transaction_checkins',
                    ),
                )
                .catch((e) => {
                    if (e.message && !e.message.includes('duplicate column name')) {
                        console.log(
                            '⚠️ [Migration] transaction_checkins formsIdsFqa migration skipped:',
                            e.message,
                        )
                    }
                })

            // Migración: Agregar forms_view a transaction_checkouts
            await db
                .execAsync(
                    'ALTER TABLE transaction_checkouts ADD COLUMN forms_view TEXT;',
                )
                .then(() =>
                    console.log(
                        '✅ [Migration] forms_view column added to transaction_checkouts',
                    ),
                )
                .catch((e) => {
                    if (e.message && !e.message.includes('duplicate column name')) {
                        console.log(
                            '⚠️ [Migration] transaction_checkouts forms_view migration skipped:',
                            e.message,
                        )
                    }
                })

            // Migración: Agregar steps_distributor a transaction_checkouts
            await db
                .execAsync(
                    'ALTER TABLE transaction_checkouts ADD COLUMN steps_distributor TEXT;',
                )
                .then(() =>
                    console.log(
                        '✅ [Migration] steps_distributor column added to transaction_checkouts',
                    ),
                )
                .catch((e) => {
                    if (e.message && !e.message.includes('duplicate column name')) {
                        console.log(
                            '⚠️ [Migration] transaction_checkouts steps_distributor migration skipped:',
                            e.message,
                        )
                    }
                })

            // Asegurar columnas relacionadas con scoring en form_question_answers
            const scoringMigrations = [
                'ALTER TABLE form_question_answers ADD COLUMN scoreTotalQuestionsPonderarFloat FLOAT;',
                'ALTER TABLE form_question_answers ADD COLUMN scoreObtainedFloat FLOAT;',
                'ALTER TABLE form_question_answers ADD COLUMN scoresByKey TEXT;',
                'ALTER TABLE form_question_answers ADD COLUMN totalByKey TEXT;',
            ]

            for (const migration of scoringMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            `✅ [Migration] Column added to form_question_answers: ${migration.split('ADD COLUMN ')[1].split(';')[0]}`,
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message
                                .toLowerCase()
                                .includes('duplicate column name')
                        ) {
                            console.log(
                                `⚠️ [Migration] form_question_answers migration skipped: ${e.message}`,
                            )
                        }
                    })
            }
            const footerMigrations = ['ALTER TABLE footer_app ADD COLUMN link TEXT;']

            for (const migration of footerMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            `✅ [Migration] Column added to footer_app: ${migration.split('ADD COLUMN ')[1].split(';')[0]}`,
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message
                                .toLowerCase()
                                .includes('duplicate column name')
                        ) {
                            console.log(
                                `⚠️ [Migration] footer_app migration skipped: ${e.message}`,
                            )
                        }
                    })
            }

            const learningrMigrations = [
                `ALTER TABLE ${TABLE_NAME.LEARNING_EVALUATIONS} ADD COLUMN allow_partial_submission BOOLEAN;`,
                `ALTER TABLE ${TABLE_NAME.LEARNING_EVALUATIONS} ADD COLUMN max_retry_attempts INTEGER;`,
                `ALTER TABLE ${TABLE_NAME.LEARNING_EVALUATIONS} ADD COLUMN retry_only_failed BOOLEAN;`,
                `ALTER TABLE ${TABLE_NAME.LEARNING_EVALUATIONS} ADD COLUMN attempts_count INTEGER;`,
                `ALTER TABLE ${TABLE_NAME.LEARNING_EVALUATIONS} ADD COLUMN failed_questions TEXT;`,
                `ALTER TABLE ${TABLE_NAME.LEARNING_EVALUATIONS} ADD COLUMN partial_anmswers TEXT;`,
                `ALTER TABLE ${TABLE_NAME.LEARNING_ANSWERS_EVALUATIONS} ADD COLUMN status INTEGER DEFAULT 0;`,
                `ALTER TABLE ${TABLE_NAME.LEARNING_ANSWERS_EVALUATIONS} ADD COLUMN evaluation_id TEXT;`,
                `ALTER TABLE ${TABLE_NAME.LEARNING_EVALUATIONS} ADD COLUMN current_attempt_id TEXT;`,
            ]

            for (const migration of learningrMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            `✅ [Migration] Column added to learning_evaluations: ${migration.split('ADD COLUMN ')[1].split(';')[0]}`,
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message
                                .toLowerCase()
                                .includes('duplicate column name')
                        ) {
                            console.log(
                                `⚠️ [Migration] learning_evaluations migration skipped: ${e.message}`,
                            )
                        }
                    })
            }

            const campaignMigrations = [
                'ALTER TABLE campaings ADD COLUMN campaing_subtitle TEXT;',
            ]

            for (const migration of campaignMigrations) {
                await db
                    .execAsync(migration)
                    .then(() => {
                        console.log(
                            `✅ [Migration] Column added to campaigns: ${migration.split('ADD COLUMN ')[1].split(';')[0]}`,
                        )
                    })
                    .catch((e) => {
                        if (
                            e.message &&
                            !e.message
                                .toLowerCase()
                                .includes('duplicate column name')
                        ) {
                            console.log(
                                `⚠️ [Migration] campaigns migration skipped: ${e.message}`,
                            )
                        }
                    })
            }
        } catch (error) {
            console.log(
                '⚠️ [Migrations] Error running migrations (may be safe to ignore):',
                error,
            )
        }
    }

    /**
     * ✅ Método principal para obtener la BD con tablas inicializadas
     * Usar este método en lugar de getInstance() directamente
     */
    public static async getRealDatabase(): Promise<SQLite.SQLiteDatabase> {
        const db = DatabaseSingleton.getInstance()

        // Asegurar que las tablas están creadas
        if (!DatabaseSingleton.tablesInitialized) {
            await DatabaseSingleton.initializeTables()
        }

        return db
    }
}

// ✅ Exportar también la función getRealDatabase como función standalone
export const getRealDatabase = () => DatabaseSingleton.getRealDatabase()

/**
 * Helper to finalize prepared statements with retries when DB is locked.
 * Exported so native modules that use prepareAsync directly can use the same retry logic.
 */
export async function finalizeStatement(statement: any): Promise<void> {
    if (!statement || typeof statement.finalizeAsync !== 'function') return

    const maxRetries = 5
    let attempt = 0

    while (true) {
        try {
            await statement.finalizeAsync()
            return
        } catch (err: any) {
            const msg = err && err.message ? String(err.message).toLowerCase() : ''
            const isLocked =
                msg.includes('database is locked') ||
                msg.includes('error code 5') ||
                err?.code === 5
            attempt++
            if (isLocked && attempt <= maxRetries) {
                // backoff a bit
                const wait = 30 * attempt
                await new Promise((res) => setTimeout(res, wait))
                continue
            }

            console.error('[DatabaseSingleton] finalizeAsync failed:', err)
            throw err
        }
    }
}

export default DatabaseSingleton
